// Onboarding step (create path only): choose a profile picture. Restore paths
// skip it — a restored account already has an avatar server-side. The choice is
// an index (0..8) carried in OnboardingResult and applied via updateMe once
// registration mints the userId. Skippable ("choose later") — the same picker
// lives in Settings, so nothing is lost by deferring.
//
// Pre-registration the real userId does not exist yet (the server assigns a
// random UUID on register), so the picker is seeded by the identity's public
// key as a stable stand-in. The chosen INDEX is what persists; the final disc
// is rendered from candidateSeed(realUserId, index) after registration.

import { useState, type ReactNode } from 'react';
import { AvatarPicker } from '../../screens/ui/AvatarPicker.js';
import { useT } from '../../i18n/index.js';

export function PickAvatar({
  userId,
  onContinue,
  onSkip,
  onBack,
}: {
  /** Stable seed for the candidate discs (the identity public key pre-register). */
  userId: string;
  onContinue: (avatarSeed: number) => void;
  onSkip: () => void;
  onBack: () => void;
}): ReactNode {
  const t = useT();
  const [selected, setSelected] = useState(0);

  return (
    <main className="mx-auto flex min-h-full w-full max-w-app flex-col gap-6 p-6">
      <h1 className="text-2xl font-semibold text-ink">{t('pickAvatar.title')}</h1>
      <p className="text-sm text-muted">{t('pickAvatar.help')}</p>
      <AvatarPicker userId={userId} value={selected} onChange={setSelected} />
      <div className="mt-auto flex flex-col gap-3">
        <div className="flex gap-3">
          <button
            type="button"
            onClick={onBack}
            className="min-h-touch flex-1 rounded-xl border border-edge bg-surface px-4 font-medium text-ink"
          >
            {t('common.back')}
          </button>
          <button
            type="button"
            onClick={() => onContinue(selected)}
            className="min-h-touch flex-1 rounded-xl bg-accent px-4 font-medium text-ink active:bg-accent-press"
          >
            {t('common.continue')}
          </button>
        </div>
        <button type="button" onClick={onSkip} className="self-center text-sm text-accent underline">
          {t('pickAvatar.skip')}
        </button>
      </div>
    </main>
  );
}
